// Web `crypto.subtle` implementation of `RecoveryAead`. PBKDF2-SHA256
// (600 000 iterations — OWASP 2023 recommended floor) → 256-bit AES key
// → AES-256-GCM with a fresh 12-byte IV.

// Explicit `.ts` so Vite's platform-extension resolver
// (`['.web.ts', '.ts', ...]`) does not collapse `'./recoveryAead'`
// back into `recoveryAead.web.ts` (this file) — same gotcha that
// `identity.web.ts` documents at the top of its imports.
import {
  bytesToUrlBase64,
  IV_LEN,
  MIN_PASSPHRASE_LEN,
  packEnvelope,
  PBKDF2_ITER,
  type RecoveryAead,
  type RecoveryEnvelopeV1,
  SALT_LEN,
  unpackEnvelope,
  urlBase64ToBytes,
} from './recoveryAead.ts';

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const pw = new TextEncoder().encode(passphrase);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    pw as unknown as BufferSource,
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: PBKDF2_ITER,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export const recoveryAead: RecoveryAead = {
  async encrypt(plaintext: Uint8Array, passphrase: string): Promise<string> {
    if (passphrase.length < MIN_PASSPHRASE_LEN) {
      throw new Error(`recovery passphrase must be at least ${MIN_PASSPHRASE_LEN} characters`);
    }
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
    const key = await deriveKey(passphrase, salt);
    const ct = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv as unknown as BufferSource },
        key,
        plaintext as unknown as BufferSource,
      ),
    );
    const envelope: RecoveryEnvelopeV1 = {
      v: 1,
      kdf: 'PBKDF2-SHA256',
      iter: PBKDF2_ITER,
      salt: bytesToUrlBase64(salt),
      alg: 'AES-256-GCM',
      iv: bytesToUrlBase64(iv),
      ct: bytesToUrlBase64(ct),
    };
    return packEnvelope(envelope);
  },

  async decrypt(ciphertext: string, passphrase: string): Promise<Uint8Array> {
    const env = unpackEnvelope(ciphertext);
    const salt = urlBase64ToBytes(env.salt);
    const iv = urlBase64ToBytes(env.iv);
    const ct = urlBase64ToBytes(env.ct);
    const key = await deriveKey(passphrase, salt);
    const pt = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      ct as unknown as BufferSource,
    );
    return new Uint8Array(pt);
  },
};
